import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar, User, Smartphone, MapPin, Wrench, Navigation2, CheckCircle2, Search, MessageSquareText } from "lucide-react";
import { getWhatsAppLink } from "@/lib/whatsapp";

// Marker icon served from CDN (avoids bundler icon-path issues)
const MARKER_ICON = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const PROBLEMS = [
  "AC - not Working",
  "AC - not Cooling",
  "AC Service",
  "Site Inspection",
  "New AC Copper Piping",
  "New AC Requirement",
  "AC Consultation",
  "HVAC Audit",
  "Others - Please Specify",
];

// Build WhatsApp message from form data (shared by preview & send)
const buildMessage = ({ today, name, mobile, address, mapLink, problem, otherProblem }) => {
  const cleanMobile = mobile.replace(/\D/g, "").slice(-10);
  const problemText =
    problem === "Others - Please Specify"
      ? `Others: ${otherProblem.trim() || "(please specify)"}`
      : problem || "(not selected)";

  return (
    `*New Breakdown / Repair Complaint*\n\n` +
    `Date: ${today}\n` +
    `Name: ${name.trim() || "(your name)"}\n` +
    `Mobile: +91 ${cleanMobile || "XXXXXXXXXX"}\n` +
    `Address: ${address.trim() || "-"}\n` +
    (mapLink ? `Location: ${mapLink}\n` : "") +
    `Problem: ${problemText}\n\n` +
    `Please register my complaint and arrange service at the earliest.`
  );
};

const ComplaintSection = () => {
  const today = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [address, setAddress] = useState("");
  const [mapLink, setMapLink] = useState("");
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);
  const [problem, setProblem] = useState("");
  const [otherProblem, setOtherProblem] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);

  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  // Reverse geocode + update coords/address (free OpenStreetMap Nominatim, no API key)
  const updateLocation = useCallback(async (lat, lng) => {
    setCoords({ lat, lng });
    setMapLink(`https://maps.google.com/?q=${lat},${lng}`);
    setSent(false);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        { headers: { Accept: "application/json" } }
      );
      const d = await res.json();
      if (d && d.display_name) setAddress(d.display_name);
    } catch (e) {
      // keep manual address if reverse geocoding fails
    }
  }, []);

  const useMyLocation = () => {
    setError("");
    if (!navigator.geolocation) {
      setError("Location is not supported on this device. Please type your address.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setLocating(false);
        updateLocation(latitude, longitude);
        if (mapRef.current && markerRef.current) {
          mapRef.current.setView([latitude, longitude], 17);
          markerRef.current.setLatLng([latitude, longitude]);
        }
      },
      () => {
        setError("Could not get location. Please allow location access or type your address.");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Forward geocode: find a place by name and move the pin there
  const handleSearch = async () => {
    const q = searchQuery.trim();
    if (!q) return;
    setError("");
    setSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=1&countrycodes=in`,
        { headers: { Accept: "application/json" } }
      );
      const arr = await res.json();
      if (arr && arr.length) {
        const lat = parseFloat(arr[0].lat);
        const lng = parseFloat(arr[0].lon);
        await updateLocation(lat, lng);
        if (mapRef.current && markerRef.current) {
          mapRef.current.setView([lat, lng], 17);
          markerRef.current.setLatLng([lat, lng]);
        }
      } else {
        setError("No location found. Try a nearby landmark, area or pincode.");
      }
    } catch (e) {
      setError("Could not search location right now. Please try again.");
    }
    setSearching(false);
  };

  // Create the interactive Leaflet map once coords are available (draggable pin)
  useEffect(() => {
    if (!coords || mapRef.current || !mapDivRef.current) return;
    const map = L.map(mapDivRef.current).setView([coords.lat, coords.lng], 16);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(map);
    const marker = L.marker([coords.lat, coords.lng], { draggable: true, icon: MARKER_ICON }).addTo(map);
    marker.on("dragend", () => {
      const p = marker.getLatLng();
      updateLocation(p.lat, p.lng);
    });
    map.on("click", (e) => {
      marker.setLatLng(e.latlng);
      updateLocation(e.latlng.lat, e.latlng.lng);
    });
    mapRef.current = map;
    markerRef.current = marker;
    setTimeout(() => map.invalidateSize(), 200);
  }, [coords, updateLocation]);

  // Destroy map on unmount
  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
    };
  }, []);

  // Live preview message (updates as user types)
  const previewMessage = useMemo(
    () => buildMessage({ today, name, mobile, address, mapLink, problem, otherProblem }),
    [today, name, mobile, address, mapLink, problem, otherProblem]
  );

  const handleSend = () => {
    setError("");
    const cleanMobile = mobile.replace(/\D/g, "").slice(-10);
    if (!name.trim()) return setError("Please enter your name.");
    if (cleanMobile.length !== 10) return setError("Please enter a valid 10-digit mobile number.");
    if (!problem) return setError("Please select the problem type.");
    if (problem === "Others - Please Specify" && !otherProblem.trim())
      return setError("Please specify your problem.");

    const msg = buildMessage({ today, name, mobile, address, mapLink, problem, otherProblem });
    window.open(getWhatsAppLink(msg), "_blank", "noopener,noreferrer");
    setSent(true);
  };

  return (
    <section
      id="complaint-registration"
      className="py-20 bg-gradient-to-br from-amber-50 via-white to-yellow-50 relative overflow-hidden"
      data-testid="complaint-section"
    >
      <div className="pointer-events-none absolute -top-16 -right-16 w-72 h-72 bg-amber-300/30 rounded-full blur-3xl" />
      <div className="pointer-events-none absolute -bottom-16 -left-16 w-80 h-80 bg-yellow-300/20 rounded-full blur-3xl" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-10 section-animate">
          <Badge className="bg-red-100 text-red-700 border-red-200 mb-4">Online Complaint</Badge>
          <h2
            className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 flex items-center justify-center gap-3 flex-wrap"
            data-testid="complaint-title"
          >
            <Wrench className="h-7 w-7 text-amber-500" />
            AC / Cassette / Ductable / VRF / Air Chiller / AHU Breakdown or Repair
          </h2>
          <p className="text-lg text-gray-600">Online Complaint Registration</p>
        </div>

        <Card className="bg-white border border-amber-200 shadow-xl">
          <CardContent className="p-6 md:p-8">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* LEFT COLUMN: Form */}
              <div className="space-y-5">
                {/* Date */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-gray-700 text-sm font-medium">
                    <Calendar className="h-4 w-4 text-amber-500" /> Date
                  </Label>
                  <Input
                    readOnly
                    value={today}
                    className="bg-amber-50 border-amber-200 text-gray-700 font-medium cursor-default"
                    data-testid="complaint-date"
                  />
                </div>

                {/* Name (Required) */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-gray-700 text-sm font-medium">
                    <User className="h-4 w-4 text-amber-500" /> Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={name}
                    placeholder="Your full name"
                    onChange={(e) => {
                      setName(e.target.value);
                      setSent(false);
                      setError("");
                    }}
                    className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus-visible:ring-amber-500"
                    data-testid="complaint-name"
                  />
                </div>

                {/* Problem (Required) - Moved right after Name */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-gray-700 text-sm font-medium">
                    <Wrench className="h-4 w-4 text-amber-500" /> Problem <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={problem}
                    onValueChange={(v) => {
                      setProblem(v);
                      setSent(false);
                      setError("");
                    }}
                  >
                    <SelectTrigger
                      className="bg-white border-gray-300 text-gray-700 focus:ring-amber-500"
                      data-testid="complaint-problem"
                    >
                      <SelectValue placeholder="Select the problem" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROBLEMS.map((p) => (
                        <SelectItem key={p} value={p}>
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {problem === "Others - Please Specify" && (
                    <Input
                      value={otherProblem}
                      placeholder="Please describe your problem"
                      onChange={(e) => {
                        setOtherProblem(e.target.value);
                        setSent(false);
                      }}
                      className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus-visible:ring-amber-500 mt-2"
                      data-testid="complaint-other"
                    />
                  )}
                </div>

                {/* Mobile Number (Required) */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-gray-700 text-sm font-medium">
                    <Smartphone className="h-4 w-4 text-amber-500" /> Mobile Number <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600 text-sm bg-gray-100 border border-gray-300 rounded-md px-3 py-2">
                      +91
                    </span>
                    <Input
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={mobile}
                      placeholder="10-digit mobile"
                      onChange={(e) => {
                        setMobile(e.target.value.replace(/\D/g, "").slice(0, 10));
                        setSent(false);
                        setError("");
                      }}
                      className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus-visible:ring-amber-500"
                      data-testid="complaint-mobile"
                    />
                  </div>
                </div>

                {/* Address (Optional) */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-gray-700 text-sm font-medium">
                    <MapPin className="h-4 w-4 text-amber-500" /> Address <span className="text-gray-400 text-xs font-normal">(optional)</span>
                  </Label>
                  <Textarea
                    value={address}
                    placeholder="Flat / building, street, area, city, pincode"
                    onChange={(e) => {
                      setAddress(e.target.value);
                      setSent(false);
                    }}
                    className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus-visible:ring-amber-500 min-h-[70px]"
                    data-testid="complaint-address"
                  />
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={useMyLocation}
                      disabled={locating}
                      className="inline-flex items-center gap-2 text-sm text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-300 rounded-md px-3 py-2 transition-colors disabled:opacity-60"
                      data-testid="complaint-use-location"
                    >
                      <Navigation2 className="h-4 w-4" />
                      {locating ? "Fetching location..." : "Use My Location (Google Maps)"}
                    </button>
                    {mapLink && (
                      <a
                        href={mapLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-green-700 underline font-medium"
                        data-testid="complaint-map-link"
                      >
                        Location pinned - open in Google Maps
                      </a>
                    )}
                  </div>

                  {/* Search a place / landmark */}
                  <div className="mt-3">
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <Input
                          value={searchQuery}
                          placeholder="Search landmark, area or pincode"
                          onChange={(e) => setSearchQuery(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleSearch();
                            }
                          }}
                          className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus-visible:ring-amber-500 pl-9"
                          data-testid="complaint-search-input"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleSearch}
                        disabled={searching}
                        className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-black font-semibold px-4 py-2 rounded-md transition-colors whitespace-nowrap"
                        data-testid="complaint-search-btn"
                      >
                        <Search className="h-4 w-4" />
                        {searching ? "Searching..." : "Search"}
                      </button>
                    </div>
                  </div>

                  {/* Selected location map (drag the pin to fine-tune) + address */}
                  {coords && (
                    <div className="mt-3 space-y-2" data-testid="complaint-map">
                      <div className="overflow-hidden rounded-lg border border-amber-300 shadow-sm">
                        <div ref={mapDivRef} className="w-full h-60" data-testid="complaint-leaflet-map" />
                      </div>
                      <p className="text-xs text-amber-700 flex items-center gap-1">
                        <Navigation2 className="h-3 w-3" />
                        Drag the pin (or tap the map) to set your exact location.
                      </p>
                      <p className="text-sm text-gray-700" data-testid="complaint-selected-address">
                        <span className="font-semibold text-amber-700">Selected address: </span>
                        {address || "Fetching address..."}
                      </p>
                    </div>
                  )}
                </div>

                {error && (
                  <p className="text-red-500 text-sm" data-testid="complaint-error">
                    {error}
                  </p>
                )}

                <button
                  type="button"
                  onClick={handleSend}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1DA851] text-white font-bold px-6 py-3 rounded-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-green-500/30"
                  data-testid="complaint-send"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  Send Now on WhatsApp
                </button>

                {sent && (
                  <div
                    className="flex items-start gap-2 text-green-700 text-sm bg-green-50 border border-green-300 rounded-lg p-3"
                    data-testid="complaint-success"
                  >
                    <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>WhatsApp is opening with your complaint. Tap Send in WhatsApp to submit it to our team.</span>
                  </div>
                )}
              </div>

              {/* RIGHT COLUMN: Live Message Preview */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-gray-700 font-medium">
                  <MessageSquareText className="h-5 w-5 text-amber-500" />
                  <span>Live Message Preview</span>
                </div>
                <div
                  className="bg-gradient-to-br from-gray-50 to-amber-50 border border-amber-200 rounded-xl p-4 shadow-inner"
                  data-testid="complaint-preview-container"
                >
                  <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                    <svg className="h-3 w-3 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    This is exactly what will be sent to WhatsApp
                  </p>
                  <pre
                    className="whitespace-pre-wrap text-sm text-gray-800 font-sans leading-relaxed bg-white border border-gray-200 rounded-lg p-4"
                    data-testid="complaint-preview-text"
                  >
                    {previewMessage}
                  </pre>
                </div>
                <p className="text-xs text-gray-500">
                  Fill in the form on the left and watch this message update in real-time. Required fields are marked with <span className="text-red-500">*</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default ComplaintSection;
